import React from 'react';
import { 
  MdSchool, 
  MdCode, 
  MdGroup, 
  MdAutoAwesome, 
  MdSecurity, 
  MdCloud, 
  MdDescription,
  MdAccountTree,
  MdPeople,
  MdAssignment,
  MdHistory,
  MdFeedback
} from 'react-icons/md';

const LandingAbout = () => {
  const coreFeatures = [
    {
      icon: <MdAccountTree className="w-6 h-6" />,
      title: 'ERD Editor',
      description: 'Create, visualize, and validate Entity Relationship Diagrams with visual and raw notation editors. Get AI-powered feedback on your database designs.'
    },
    {
      icon: <MdDescription className="w-6 h-6" />,
      title: 'Document Editor',
      description: 'Rich markdown editor with collaborative editing, code execution, Q&A integration, and comprehensive course material management.'
    },
    {
      icon: <MdCode className="w-6 h-6" />,
      title: 'Code Editor',
      description: 'Interactive code editor with syntax highlighting, in-browser execution (Python, MATLAB/Octave), AI feedback, and project management.'
    }
  ];

  const additionalFeatures = [
    {
      icon: <MdSchool className="w-6 h-6" />,
      title: 'Class Management',
      description: 'Create and manage classes with enrollment, role-based access, tool configuration, and Canvas integration.'
    },
    {
      icon: <MdPeople className="w-6 h-6" />,
      title: 'Student Management',
      description: 'Track student progress, manage enrollments, view analytics, and handle bulk operations with CSV import.'
    },
    {
      icon: <MdAssignment className="w-6 h-6" />,
      title: 'Assignment Creation',
      description: 'Create assignments with rubrics, due dates, multiple submission types, and automated grading capabilities.'
    },
    {
      icon: <MdHistory className="w-6 h-6" />,
      title: 'Version Control',
      description: 'Track changes, view history, restore previous versions, and maintain complete revision history for all your work.'
    },
    {
      icon: <MdAutoAwesome className="w-6 h-6" />,
      title: 'AI-Powered Learning',
      description: 'Get intelligent feedback, suggestions, and assistance across ERD designs, code, and documents powered by advanced AI.'
    },
    {
      icon: <MdFeedback className="w-6 h-6" />,
      title: 'Q&A & Collaboration',
      description: 'Engage with instructors and peers through Q&A panels, discussions, threaded feedback, and real-time collaboration.'
    },
    {
      icon: <MdSecurity className="w-6 h-6" />,
      title: 'Secure & Private',
      description: 'Enterprise-grade security with role-based access control, encrypted storage, and comprehensive privacy protection.'
    },
    {
      icon: <MdCloud className="w-6 h-6" />,
      title: 'Cloud-Based',
      description: 'Access your work from anywhere with automatic saving, real-time synchronization, and reliable cloud infrastructure.'
    }
  ];

  return (
    <div className="py-20">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">About the Platform</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          AI Assist is a comprehensive learning management system designed to enhance 
          education through interactive tools, AI-powered assistance, and seamless collaboration 
          across multiple learning domains.
        </p>
      </div>

      {/* Core Features */}
      <div className="mb-20">
        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Core Features</h3>
        <div className="grid md:grid-cols-3 gap-8">
          {coreFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition-colors shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">{feature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Features */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Additional Capabilities</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {additionalFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-2xl p-12 border border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Built for Education</h3>
          <p className="text-gray-600 leading-relaxed mb-6">
            AI Assist was developed at the University of Illinois Chicago to provide 
            students and instructors with modern, interactive tools for comprehensive learning. 
            The platform combines traditional learning methods with cutting-edge AI technology 
            to support ERD design, coding, document creation, and collaborative learning across 
            multiple disciplines.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span>React-based Interface</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span>RESTful API Architecture</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span>Cloud Document Storage</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingAbout;

